import { ApexOptions } from 'apexcharts';
import { useCallback, useMemo, useRef } from 'react';
import { nFormatter } from '../helpers/nFormater';
import {
  $lastPeriodSelection,
  $seriesState,
  fetchMixingAnalitycDataChartFx,
  updateLastPeriodSelection,
  updateSeriesState,
} from '@/widgets/analytics/model';
import { PERIOD_CONFIG } from '../config/config';
import { COLORS_CHART } from '../constans/colors';
import { useStore } from 'effector-react';
import ApexCharts from 'apexcharts';
import { maxLength } from '../helpers/maxLength';

export const useLineChartOptions = (
  categories: string[],
  dates: number[],
  numberDays: number,
  seriesChart: any[],
  benefit: number[],
) => {
  const seriesState = useStore($seriesState);
  const timerRefZoom = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback((value: number) => {
    if (timerRefZoom.current) {
      clearTimeout(timerRefZoom.current);
    }

    timerRefZoom.current = setTimeout(() => {
      fetchMixingAnalitycDataChartFx(value);
    }, 500);
  }, []);

  const handleChartEvent = useCallback((chartEl: any, chart: any) => {
    chartEl.toolbar.localeValues.reset =
      'Сбросить масштаб к исходному состоянию 7 дней';
    const xMin = chart?.globals.minX >= 1 ? chart?.globals.minX : 1;
    const xMax = chart?.globals.maxX;
    const currentPeriod = $lastPeriodSelection.getState();

    if (currentPeriod?.max !== xMax || currentPeriod?.min !== Math.round(xMin)) {
      updateLastPeriodSelection({ min: Math.round(xMin), max: xMax });
    }
  }, []);

  const handleChartEventZoomed = useCallback(
    async (chart: any, { xaxis, yaxis }: any) => {
      const chartMaxLength = maxLength(chart);
      const timeoutDuration = chartMaxLength > 500 ? 2500 : 1500;

      if (!yaxis) {
        handleChange(7);

        if (numberDays > 10) {
          await new Promise((resolve) => setTimeout(resolve, timeoutDuration));
        }
        const currentMaxLength = maxLength(chart);
        updateLastPeriodSelection({
          min: 1,
          max: currentMaxLength,
        });

        ApexCharts.exec('chart1', 'updateOptions', {
          chart: {
            selection: {
              enabled: true,
              xaxis: {
                min: 1,
                max: currentMaxLength,
              },
            },
          },
        });
      }

      const currentZoom = Math.round(xaxis.max - xaxis.min + 1);
      const currentZoomSeries = seriesChart[0].data.length;

      if (xaxis.min !== undefined && xaxis.max !== undefined) {
        updateLastPeriodSelection({
          min: xaxis.min,
          max: chartMaxLength >= xaxis.max ? xaxis.max : chartMaxLength,
        });

        ApexCharts.exec('chart1', 'updateOptions', {
          chart: {
            selection: {
              enabled: true,
              xaxis: {
                min: xaxis.min,
                max: chartMaxLength >= xaxis.max ? xaxis.max : chartMaxLength,
              },
            },
          },
        });
      }

      if (currentZoom !== currentZoomSeries) {
        return;
      }
      //@ts-ignore
      const period: number = PERIOD_CONFIG.dayRanges.find(
        ({ rangeStart, rangeEnd }) =>
          numberDays >= rangeStart && numberDays <= rangeEnd,
      )?.period;

      if (yaxis !== undefined) {
        handleChange(period);
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      const currentMaxLength = maxLength(chart);

      updateLastPeriodSelection({ min: 1, max: currentMaxLength });

      ApexCharts.exec('chart1', 'updateOptions', {
        chart: {
          selection: {
            enabled: true,
            xaxis: {
              min: 1,
              max: currentMaxLength,
            },
          },
        },
      });
    },
    [numberDays, seriesChart],
  );
  const handleLegendClick = useCallback(
    (chartEl: any, seriesIndex: number, opts: any) => {
      const seriesName = chartEl.w.config.series[seriesIndex].name;

      updateSeriesState({
        seriesName,
        disabled: !seriesState.series.find((s) => s.name === seriesName)?.disabled,
      });

      ['chart1', 'chart2'].forEach((chartId) => {
        ApexCharts.exec(chartId, 'toggleSeries', seriesName);
      });
    },
    [seriesChart],
  );
  return useMemo(
    () =>
      ({
        chart: {
          id: 'chart2',
          type: 'line',
          height: 250,
          dropShadow: {
            enabled: true,
            enabledOnSeries: [1],
          },
          animations: { enabled: false },
          redrawOnWindowResize: false,
          toolbar: {
            autoSelected: 'zoom',
            show: true,
            offsetY: -18,
            tools: {
              download: false,
              selection: false,
              pan: false,
              reset: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
            },
          },
          zoom: {
            enabled: true,
            type: 'x',
            autoScaleYaxis: true,
            sensitivity: 1,
          },

          events: {
            legendClick: handleLegendClick,
            zoomed: handleChartEventZoomed,
            updated: handleChartEvent,
          },
        },
        performanceMetricsEnabled: true,
        colors: COLORS_CHART,
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: [1, 1, 1],
          curve: 'smooth',
        },
        fill: {
          colors: COLORS_CHART,
          opacity: 0.0001,
          type: 'solid',
        },
        markers: {
          size: 2,
          colors: COLORS_CHART,
          strokeWidth: 0,
        },

        yaxis: {
          seriesName: 'Стоимость с миксованием',
          axisTicks: { show: true },
          axisBorder: { show: true },
          labels: {
            formatter(val: number): string | string[] {
              return val.toLocaleString('RU-ru', { maximumFractionDigits: 0 }) + ' ₽';
            },
          },
          title: { text: 'Стоимость', style: { color: '#808080' } },
        },

        xaxis: {
          categories: categories,
          tickAmount: 20,
          labels: {
            showDuplicates: false,
            rotate: -45,
            hideOverlappingLabels: true,
            format: 'MMM D',
          },
          min: 0,
          tickPlacement: 'on',
        },
        tooltip: {
          enabled: true,
          shared: true,
          hideEmptySeries: false,
          intersect: false,
          y: {
            formatter: function (val, { seriesIndex, series, dataPointIndex }) {
              const extraValue =
                seriesIndex === 0
                  ? `<span style="color: #66B28A"> (+ ${nFormatter(
                      benefit[dataPointIndex],
                      2,
                    )})</span>`
                  : '';
              return val === undefined
                ? val
                : val.toLocaleString('RU-ru', { maximumFractionDigits: 0 }) +
                    ' ₽' +
                    extraValue;
            },
          },
          x: {
            show: true,
            formatter: function (val) {
              const dateObject = new Date(dates[val - 1]);
              const offset = dateObject.getTimezoneOffset();
              const localDate = new Date(dateObject.getTime() - offset * 60000); //Умножение на 60000 преобразует минуты в миллисекунды
              const optionsDate: Intl.DateTimeFormatOptions = {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              };
              return localDate.toLocaleDateString('ru-RU', optionsDate);
            },
          },
        },
      } as ApexOptions),
    [categories, numberDays, seriesChart],
  );
};
export const useAreaChartOptions = (
  categories: string[],
  dates: number[],
  twoDays: number,
) => {
  const currentPeriod = $lastPeriodSelection.getState();

  const handleResetZoom = useCallback((chartEl: any, opts: any) => {
    const xMin = chartEl?.w.globals.minX;
    const xMax = chartEl?.w.globals.maxX;
    const yMin = chartEl?.w.globals.minY;
    const yMax = chartEl?.w.globals.maxY;

    chartEl.updateOptions({
      chart: {
        selection: {
          enabled: true,
          xaxis: {
            min: xMin,
            max: xMax,
          },
        },
      },
    });
    ApexCharts.exec('chart2', 'updateOptions', {
      yaxis: {
        min: yMin,
        max: yMax,
        axisTicks: { show: true },
        axisBorder: { show: true },
        labels: {
          formatter(val: number): string | string[] {
            return val.toLocaleString('RU-ru', { maximumFractionDigits: 0 }) + ' ₽';
          },
        },
        title: { text: 'Стоимость', style: { color: '#808080' } },
      },
    });
  }, []);
  const handleUpdate = useCallback((chart: any, options?: any) => {
    chart.toolbar.localeValues.reset =
      'Сбросить выделенный масштаб к исходному состоянию';
    chart.toolbar.localeValues.selection = 'Показать изменения за последнии 2 дня';
  }, []);

  const ELEMENT_SELECT = 'apexcharts-selection-icon apexcharts-selected';
  return useMemo(
    () =>
      ({
        chart: {
          id: 'chart1',
          height: 150,
          type: 'area',
          brush: {
            target: 'chart2',
            enabled: true,
            autoScaleYaxis: true,
          },
          animations: { enabled: false },
          redrawOnWindowResize: false,
          toolbar: {
            autoSelected: 'selection',
            show: true,
            offsetY: 0,
            tools: {
              download: false,
              selection: true,
              pan: false,
              reset: true,
              zoom: false,
              zoomin: false,
              zoomout: false,
            },
          },
          zoom: {
            enabled: true,
            type: 'xy',
          },
          selection: {
            enabled: true,
            type: 'x',
            xaxis: {
              min: currentPeriod?.min,
              max: currentPeriod?.max,
            },
            yaxis: {
              min: 0,
            },
          },
          events: {
            updated: handleUpdate,
            zoomed: handleResetZoom,
            click: function (event, chartContext, opts) {
              if (event.target.className == ELEMENT_SELECT) {
                const chartMaxLength = maxLength(chartContext);
                chartContext.updateOptions({
                  chart: {
                    selection: {
                      enabled: true,
                      xaxis: {
                        min: twoDays,
                        max: chartMaxLength,
                      },
                    },
                  },
                });
              }
            },
          },
        },
        performanceMetricsEnabled: true,
        colors: COLORS_CHART,
        stroke: {
          width: [1, 1, 1],
          curve: 'smooth',
        },

        legend: {
          show: false,
        },
        xaxis: {
          tooltip: { enabled: false },
          categories: categories,
          tickAmount: 27,
        },
        yaxis: {
          tickAmount: 2,
          forceNiceScale: false,

          max: function (max) {
            return max * 1.05; // 5% от диапазона
          },
          min: function (min) {
            return min > 0 ? min * 0.95 : 0; // - 5% от диапазона
          },
          labels: {
            formatter(val: number): string | string[] {
              return val.toLocaleString('RU-ru', { maximumFractionDigits: 0 }) + ' ₽';
            },
          },
        },
      } as ApexOptions),
    [categories, dates, currentPeriod],
  );
};
